import { useMemo } from "react";
import { useDebouncedState } from "../../../../utilities/hooks/useDebouncedState";
import { useShopifyProductList } from "../../../../api/queries/useShopifyProductList";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";
import { uiNotification } from "../../../../services/UINotificationService";
import { useQueryParamsState } from "../../../../utilities/hooks/useQueryParamsState";

export function useData() {
  const { tInventory } = useAppTranslation.Inventory();
  const [search, debouncedSearch, setSearch] = useDebouncedState("");
  const [queryState, setQueryState] = useQueryParamsState({ mapped: false });

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useShopifyProductList({
    payload: {
      pageSize: 20,
      search: debouncedSearch,
      filterMapped: queryState.mapped === "true",
    },
    options: {
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: true,
      onError: () => {
        uiNotification.error(tInventory("shopify_product.error.fetch"));
      },
    },
  });

  const preparedData = useMemo(() => {
    if (data?.pages) {
      return data.pages.map((p) => p.list).reduce((r, p) => [...r, ...p], []);
    }
    return [];
  }, [data?.pages]);

  const isSoftLoading =
    !!debouncedSearch && isFetching && !isLoading && !isFetchingNextPage;

  return {
    data: {
      value: preparedData,
      hasMore: Boolean(hasNextPage),
      isNextFetching: isFetchingNextPage,
      isLoading: isLoading,
      fetchMore: fetchNextPage,
      isSoftLoading,
    },
    search: {
      value: search,
      onChange: setSearch,
      isLoading: !!debouncedSearch && isFetching && !isFetchingNextPage,
    },
    filterMapped: {
      value: queryState.mapped === "true",
      toggle: () => {
        setQueryState({ mapped: queryState.mapped === "true" ? false : true });
      },
    },
  };
}
